import type { CSSProperties } from 'react';

import { css } from '@compiled/react';

import { media } from '@atlaskit/primitives';

import { Adg4Skeleton } from '@/components/adg4/skeleton/src/skeleton';

import { ResponsiveGapOptions } from '../../container/src/container';

type ViewportSize = 'mobile' | 'tablet' | 'desktop';

/**
 * Author-composed loading placeholder for Uniform. Content-agnostic: authors
 * drop one or more into a slot (e.g. the contextual-bandits container's
 * `loading` slot) to approximate the eventual layout while content loads.
 *
 * Mobile-first and responsive. Sizing uses flat per-breakpoint params
 * (`*`/`tablet*`/`desktop*`, like `UniformImageV2`); every unset breakpoint
 * cascades up from mobile. `gap` reuses `UniformContainer`'s
 * `ResponsiveGapOptions`. Breakpoints: `media.above.sm` / `media.above.md`.
 */
export interface UniformSkeletonProps {
	/** Width of each bone. Defaults to `100%` (fills the container). */
	width?: string;
	tabletWidth?: string;
	desktopWidth?: string;
	/** Caps the width (e.g. `640px`). Defaults to `100%`. */
	maxWidth?: string;
	tabletMaxWidth?: string;
	desktopMaxWidth?: string;
	/** Height of each bone. Ignored where `aspectRatio` is set. */
	height?: string;
	tabletHeight?: string;
	desktopHeight?: string;
	/** e.g. `16 / 9`. Where set, the height derives from the width. */
	aspectRatio?: string;
	tabletAspectRatio?: string;
	desktopAspectRatio?: string;
	/** Corner radius. Use `50%` with a `1 / 1` aspectRatio for a circle. */
	borderRadius?: string;
	/** Number of stacked bones. Defaults to 1. */
	count?: number | string;
	tabletCount?: number | string;
	desktopCount?: number | string;
	/**
	 * Gap between bones on the `--ds-space-*` scale. Defaults to `150`.
	 * Partial because Uniform may author only some breakpoints (e.g. mobile).
	 */
	gap?: Partial<ResponsiveGapOptions>;
	/** Width of the final bone (the typical short last line). */
	lastLineWidth?: string;
	tabletLastLineWidth?: string;
	desktopLastLineWidth?: string;
	align?: 'start' | 'center' | 'end' | 'stretch';
}

const DEFAULT_LINE_HEIGHT = '1.25rem';
const DEFAULT_WIDTH = '100%';
const DEFAULT_MAX_WIDTH = '100%';
const DEFAULT_GAP = '150';
const DEFAULT_COUNT = 1;

const ALIGN_TO_FLEX: Record<NonNullable<UniformSkeletonProps['align']>, string> = {
	start: 'flex-start',
	center: 'center',
	end: 'flex-end',
	stretch: 'stretch',
};

type Resolved<T> = Record<ViewportSize, T>;

// Breakpoint rules read CSS custom properties so the static `css` stays
// build-time-extractable while per-bone values are supplied inline.
const containerStyles = css({
	display: 'flex',
	flexDirection: 'column',
	width: '100%',
	rowGap: 'var(--wac-skeleton-gap-mobile)',
	[media.above.sm]: {
		rowGap: 'var(--wac-skeleton-gap-tablet)',
	},
	[media.above.md]: {
		rowGap: 'var(--wac-skeleton-gap-desktop)',
	},
});

const boneStyles = css({
	position: 'relative',
	overflow: 'hidden',
	// `display` toggles per-breakpoint visibility (responsive `count`).
	display: 'var(--wac-skeleton-display-mobile)',
	width: 'var(--wac-skeleton-width-mobile)',
	maxWidth: 'var(--wac-skeleton-max-width-mobile)',
	height: 'var(--wac-skeleton-height-mobile)',
	aspectRatio: 'var(--wac-skeleton-aspect-mobile)',
	[media.above.sm]: {
		display: 'var(--wac-skeleton-display-tablet)',
		width: 'var(--wac-skeleton-width-tablet)',
		maxWidth: 'var(--wac-skeleton-max-width-tablet)',
		height: 'var(--wac-skeleton-height-tablet)',
		aspectRatio: 'var(--wac-skeleton-aspect-tablet)',
	},
	[media.above.md]: {
		display: 'var(--wac-skeleton-display-desktop)',
		width: 'var(--wac-skeleton-width-desktop)',
		maxWidth: 'var(--wac-skeleton-max-width-desktop)',
		height: 'var(--wac-skeleton-height-desktop)',
		aspectRatio: 'var(--wac-skeleton-aspect-desktop)',
	},
});

// Uniform sends "" (not undefined) for an unset field; treat both as "no value".
const orFallback = (value: string | undefined, fallback: string): string =>
	value === undefined || value === '' ? fallback : value;

const toCount = (value: number | string | undefined): number | undefined => {
	if (value === undefined || value === '') return undefined;
	const numeric = typeof value === 'string' ? Number(value) : value;
	return Number.isFinite(numeric) && numeric > 0 ? Math.floor(numeric) : undefined;
};

const resolveFlatCount = (
	mobile: number | string | undefined,
	tablet: number | string | undefined,
	desktop: number | string | undefined,
): Resolved<number> => {
	const mobileValue = toCount(mobile) ?? DEFAULT_COUNT;
	const tabletValue = toCount(tablet) ?? mobileValue;
	const desktopValue = toCount(desktop) ?? tabletValue;
	return { mobile: mobileValue, tablet: tabletValue, desktop: desktopValue };
};

// Cascades unset breakpoints up from mobile.
const resolveFlat = (
	mobile: string | undefined,
	tablet: string | undefined,
	desktop: string | undefined,
	fallback: string,
): Resolved<string> => {
	const mobileValue = orFallback(mobile, fallback);
	const tabletValue = orFallback(tablet, mobileValue);
	const desktopValue = orFallback(desktop, tabletValue);
	return { mobile: mobileValue, tablet: tabletValue, desktop: desktopValue };
};

// Tolerates a partial object (Uniform may only author the mobile value).
const resolveGap = (gap: Partial<ResponsiveGapOptions> | undefined): Resolved<string> => {
	const value: Partial<ResponsiveGapOptions> = gap ?? {};
	const mobile = orFallback(value.mobile, DEFAULT_GAP);
	const tablet = orFallback(value.tablet, mobile);
	const desktop = orFallback(value.desktop, tablet);
	return {
		mobile: `var(--ds-space-${mobile})`,
		tablet: `var(--ds-space-${tablet})`,
		desktop: `var(--ds-space-${desktop})`,
	};
};

const UniformSkeleton = ({
	width,
	tabletWidth,
	desktopWidth,
	maxWidth,
	tabletMaxWidth,
	desktopMaxWidth,
	height,
	tabletHeight,
	desktopHeight,
	aspectRatio,
	tabletAspectRatio,
	desktopAspectRatio,
	borderRadius,
	count,
	tabletCount,
	desktopCount,
	gap,
	lastLineWidth,
	tabletLastLineWidth,
	desktopLastLineWidth,
	align = 'stretch',
}: UniformSkeletonProps) => {
	const counts = resolveFlatCount(count, tabletCount, desktopCount);
	const maxCount = Math.max(counts.mobile, counts.tablet, counts.desktop);

	const widths = resolveFlat(width, tabletWidth, desktopWidth, DEFAULT_WIDTH);
	const maxWidths = resolveFlat(maxWidth, tabletMaxWidth, desktopMaxWidth, DEFAULT_MAX_WIDTH);
	// "" means "no override": fall back to the normal width / no aspect ratio.
	const lastWidths = resolveFlat(lastLineWidth, tabletLastLineWidth, desktopLastLineWidth, '');
	const aspectRatios = resolveFlat(aspectRatio, tabletAspectRatio, desktopAspectRatio, '');
	const heights = resolveFlat(height, tabletHeight, desktopHeight, DEFAULT_LINE_HEIGHT);
	const gaps = resolveGap(gap);

	// Where an aspectRatio is set the height is `auto`; otherwise the fixed one.
	const heightAt = (viewport: ViewportSize): string =>
		aspectRatios[viewport] ? 'auto' : heights[viewport];
	const aspectAt = (viewport: ViewportSize): string => aspectRatios[viewport] || 'auto';

	const containerStyle = {
		'--wac-skeleton-gap-mobile': gaps.mobile,
		'--wac-skeleton-gap-tablet': gaps.tablet,
		'--wac-skeleton-gap-desktop': gaps.desktop,
		alignItems: ALIGN_TO_FLEX[align],
	} as CSSProperties;

	// The last visible bone gets the short last-line width, when one is set.
	const widthAt = (index: number, viewport: ViewportSize): string => {
		const isLastVisible = counts[viewport] > 1 && index === counts[viewport] - 1;
		return isLastVisible && lastWidths[viewport] ? lastWidths[viewport] : widths[viewport];
	};

	const displayAt = (index: number, viewport: ViewportSize): string =>
		index < counts[viewport] ? 'block' : 'none';

	const buildBoneStyle = (index: number): CSSProperties =>
		({
			'--wac-skeleton-display-mobile': displayAt(index, 'mobile'),
			'--wac-skeleton-display-tablet': displayAt(index, 'tablet'),
			'--wac-skeleton-display-desktop': displayAt(index, 'desktop'),
			'--wac-skeleton-width-mobile': widthAt(index, 'mobile'),
			'--wac-skeleton-width-tablet': widthAt(index, 'tablet'),
			'--wac-skeleton-width-desktop': widthAt(index, 'desktop'),
			'--wac-skeleton-max-width-mobile': maxWidths.mobile,
			'--wac-skeleton-max-width-tablet': maxWidths.tablet,
			'--wac-skeleton-max-width-desktop': maxWidths.desktop,
			'--wac-skeleton-height-mobile': heightAt('mobile'),
			'--wac-skeleton-height-tablet': heightAt('tablet'),
			'--wac-skeleton-height-desktop': heightAt('desktop'),
			'--wac-skeleton-aspect-mobile': aspectAt('mobile'),
			'--wac-skeleton-aspect-tablet': aspectAt('tablet'),
			'--wac-skeleton-aspect-desktop': aspectAt('desktop'),
			borderRadius,
		}) as CSSProperties;

	return (
		<div css={containerStyles} style={containerStyle} aria-hidden="true">
			{Array.from({ length: maxCount }, (_, index) => (
				<div key={index} css={boneStyles} style={buildBoneStyle(index)}>
					<Adg4Skeleton width="100%" height="100%" />
				</div>
			))}
		</div>
	);
};

export default UniformSkeleton;
